import { z } from 'zod';

// Rückfragen mit Fragebogen: strukturierte Antworten direkt im Chat, ähnlich
// einer WhatsApp-Umfrage, aber mit allen nötigen Eingabetypen.

export const FORM_FIELD_TYPES = ['radio', 'checkbox', 'select', 'text', 'textarea', 'number', 'date', 'file'] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FIELD_LABEL: Record<FormFieldType, string> = {
  radio: 'Einfachauswahl',
  checkbox: 'Mehrfachauswahl',
  select: 'Auswahlliste',
  text: 'Kurze Antwort',
  textarea: 'Längere Antwort',
  number: 'Zahl',
  date: 'Datum',
  file: 'Datei oder Foto',
};

export const CHOICE_TYPES: FormFieldType[] = ['radio', 'checkbox', 'select'];

export const formFieldSchema = z.object({
  id: z.string().min(1).max(40),
  type: z.enum(FORM_FIELD_TYPES),
  label: z.string().trim().min(1).max(300),
  help: z.string().max(2000).nullish(),
  required: z.boolean().optional(),
  options: z.array(z.string().trim().min(1).max(200)).max(30).optional(),
  /** Bei Auswahlfeldern zusätzlich eine eigene Antwort erlauben */
  allowOther: z.boolean().optional(),
});

export const questionFormSchema = z
  .object({
    fields: z.array(formFieldSchema).min(1).max(30),
    /** Globale Sprachantwort statt Tippen erlauben */
    allowVoice: z.boolean().optional(),
    /** Antworten der anderen erst nach der eigenen Antwort zeigen */
    hideResults: z.boolean().optional(),
  })
  .superRefine((form, ctx) => {
    const ids = new Set<string>();
    form.fields.forEach((f, i) => {
      if (ids.has(f.id)) ctx.addIssue({ code: 'custom', path: ['fields', i, 'id'], message: 'Doppelte Feld-ID' });
      ids.add(f.id);
      if (CHOICE_TYPES.includes(f.type) && (f.options?.length ?? 0) < 2) {
        ctx.addIssue({ code: 'custom', path: ['fields', i, 'options'], message: `„${f.label}“ braucht mindestens zwei Antwortmöglichkeiten` });
      }
    });
  });

export type FormField = z.infer<typeof formFieldSchema>;
export type QuestionForm = z.infer<typeof questionFormSchema>;

/**
 * Antwortwerte: Auswahl = Option (radio/select) bzw. Optionen (checkbox),
 * eigene Antwort als `other:<Text>`; Text/Datum als String; Zahl als Zahl;
 * Datei als Liste von Medien-IDs.
 */
export type FormAnswer = string | number | string[] | null;
export type FormAnswers = Record<string, FormAnswer>;

export const formAnswersSchema = z.record(
  z.string().max(40),
  z.union([z.string().max(10000), z.number(), z.array(z.string().max(500)).max(30), z.null()]),
);

export const OTHER_PREFIX = 'other:';

export function isEmptyAnswer(v: FormAnswer | undefined): boolean {
  return v == null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Prüft Antworten gegen den Fragebogen. Liefert eine verständliche Fehlermeldung oder null. */
export function validateFormAnswers(form: QuestionForm, answers: FormAnswers, hasVoice: boolean): string | null {
  for (const key of Object.keys(answers)) {
    if (!form.fields.some((f) => f.id === key)) return 'Antwort auf eine unbekannte Frage';
  }
  for (const f of form.fields) {
    const v = answers[f.id];
    if (isEmptyAnswer(v)) {
      // Mit Sprachantwort sind Pflichtfelder nicht zwingend
      if (f.required && !hasVoice) return `Bitte „${f.label}“ beantworten`;
      continue;
    }
    const options = f.options ?? [];
    const okChoice = (s: string) => options.includes(s) || (!!f.allowOther && s.startsWith(OTHER_PREFIX) && s.length > OTHER_PREFIX.length);
    switch (f.type) {
      case 'radio':
      case 'select':
        if (typeof v !== 'string' || !okChoice(v)) return `Ungültige Auswahl bei „${f.label}“`;
        break;
      case 'checkbox':
        if (!Array.isArray(v) || !v.every(okChoice)) return `Ungültige Auswahl bei „${f.label}“`;
        break;
      case 'number':
        if (typeof v !== 'number' || !Number.isFinite(v)) return `Bei „${f.label}“ bitte eine Zahl angeben`;
        break;
      case 'date':
        if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return `Bei „${f.label}“ bitte ein Datum angeben`;
        break;
      case 'file':
        if (!Array.isArray(v)) return `Ungültige Datei bei „${f.label}“`;
        break;
      default:
        if (typeof v !== 'string') return `Bei „${f.label}“ bitte Text angeben`;
    }
  }
  return null;
}

/** Medien, auf die eine Antwort verweist (Dateien und Sprachantwort). */
export function answerMediaIds(form: QuestionForm | null | undefined, answers: FormAnswers, voiceMediaId?: string | null): string[] {
  const ids = form ? form.fields.filter((f) => f.type === 'file').flatMap((f) => (Array.isArray(answers[f.id]) ? (answers[f.id] as string[]) : [])) : [];
  return voiceMediaId ? [...ids, voiceMediaId] : ids;
}

/** Lesbarer Wert einer Antwort (für Listen, Suche und KI-Clients). */
export function answerText(field: FormField, v: FormAnswer | undefined): string {
  if (isEmptyAnswer(v)) return '';
  const show = (s: string) => (s.startsWith(OTHER_PREFIX) ? s.slice(OTHER_PREFIX.length) : s);
  if (field.type === 'file') return `${(v as string[]).length} Datei(en)`;
  if (field.type === 'date' && typeof v === 'string') {
    const [y, m, d] = v.split('-');
    return `${d}.${m}.${y}`;
  }
  if (Array.isArray(v)) return v.map(show).join(', ');
  return typeof v === 'string' ? show(v) : String(v);
}

export interface ChoiceResult {
  option: string;
  count: number;
  names: string[];
  other?: boolean;
}

/** Auswertung eines Auswahlfeldes für die Balkenanzeige (inklusive eigener Antworten). */
export function choiceResults(field: FormField, responses: { userName: string; answers: FormAnswers }[]): { results: ChoiceResult[]; total: number } {
  const results: ChoiceResult[] = (field.options ?? []).map((option) => ({ option, count: 0, names: [] }));
  let total = 0;
  for (const r of responses) {
    const v = r.answers[field.id];
    const picked = Array.isArray(v) ? v : typeof v === 'string' ? [v] : [];
    if (picked.length) total++;
    for (const p of picked) {
      if (p.startsWith(OTHER_PREFIX)) {
        results.push({ option: p.slice(OTHER_PREFIX.length), count: 1, names: [r.userName], other: true });
        continue;
      }
      const hit = results.find((x) => x.option === p && !x.other);
      if (hit) (hit.count++, hit.names.push(r.userName));
    }
  }
  return { results, total };
}

let counter = 0;
/** Kurze, im Fragebogen eindeutige Feld-ID. */
export function newFieldId(): string {
  counter = (counter + 1) % 1000;
  return `f${Date.now().toString(36)}${counter.toString(36)}`;
}
